import { observable, action } from "mobx";
import { ArrayHelper, QAEventInfo, PushMessageTypes } from "sb-resp-lib";
import { Parser } from "../parsers/Parser";
import { QAEventInfoExt, QALiveSlipSelection, PulseBetMarketComponentData, PulseBetMarketAdditionalData, PulseBetPeriod, PulseFreeBetInfo } from "../models";
import { StakeViewModel, StakeCurrencyContainer, BetPlacementStatus } from "../../blocks/PulseBetMarkets/data";//TO DO  STORE should not use local block objects
import Debounce from "debounce-decorator";

export type PageMethodsComposite = {
    pageMethods: PageMethods,
    masterEventsPageMethoods: MasterEventsPageMethods,
    bettingHistoryPageMethods: BettingHistoryPageMethods,
    bettingPageMethods: BettingPageMethods
};

export type EventModel = {
    id: string;
    type: number;
};

export class PulseBettingStore {

    private static instance: PulseBettingStore;
    private facade!: Facade;
    private name = "PulseBettingStore";
    private branchId: string;
    private updateEventsTimeout: any;
    public dataComposite!: PageMethodsComposite;
    public masterEventId: string = "0";
    public currencySign!: string;
    public eventsToUpdate: PushFacadeElements[];
    public oddsUtil!: Odds;
    public betslipUtil!: BetSlipUtil;
    eventsManager: EventsManager;
    navHandler: any;

    @observable public isUserLogged: boolean;
    @observable public currency!: StakeCurrencyContainer;
    @observable public eventIds: EventModel[];
    @observable public events: QAEventInfoExt[];
    @observable public infoMessages: string[];
    @observable public pulsebetsDataForComponents: PulseBetMarketComponentData[];
    @observable awaitingCallback: any[];

    constructor() {
        this.eventsToUpdate = [];
        this.events = [];
        this.infoMessages = [];
        this.successfullUpdateEventsCallback = this.successfullUpdateEventsCallback.bind(this);
        this.pulsebetsDataForComponents = [];
        this.branchId = "1";
        this.eventIds = [];
        this.isUserLogged = false;
        this.awaitingCallback = [];
        this.eventsManager = new EventsManager();
    }

    clear() {
        this.eventIds = [];
        this.events = [];
        this.infoMessages = [];
        clearTimeout(this.updateEventsTimeout);
        this.pulsebetsDataForComponents = [];
        this.eventsManager.removeEventListener("onPulseBetWaitingResponse", this, true);
        this.eventsManager.removeEventListener("onBonusMoneyBetting", this, true);
    }

    setFacade(facade: Facade) {
        if (typeof this.facade === "undefined" && facade) {
            this.facade = facade;
            this.facade.addSubscriber(this.subscribeToPush, this.name, PushMessageTypes.Odds, {
                SubscriberEvents:
                    this.eventsToUpdate, IsBetSlip: false
            })
        }
    }

    setEvents(eventIds: EventModel[]) {
        this.eventIds = eventIds;
        for (let ev of eventIds) {
            this.eventsToUpdate[ev.id] = {
                ID: ev.id,
                updateString: ev.id.toString(),
                /* istanbul ignore next */
                getUpdateString(): string {
                    /* istanbul ignore next */
                    return this.updateString;
                }
            };
        }
    }

    createStakeViewModel(eventId: string): StakeViewModel {
        let index = this.currency.pulseBettingStakeValuesSeries.findIndex(stake => stake === this.currency.defaultStake);

        return new StakeViewModel({
            stakeValue: this.currency.defaultStake,
            currencySign: this.currencySign,
            eventId,
            index,
            isMinusDisabled: index <= 0,
            isPlusDisabled: index === this.currency.pulseBettingStakeValuesSeries.length - 1
        });
    }

    removeFromWaitingBets(eventId: number) {
        let index = this.awaitingCallback.findIndex(bet => bet.eventId === eventId);
        if (index >= 0) {
            this.awaitingCallback.splice(index, 1);
        }
        if (eventId === -1) {
            this.awaitingCallback.splice(0, 1);
        }

        if (this.awaitingCallback.length) {
            setTimeout(this.awaitingCallback[0].func, 250);
        }
    }

    addToMessages(messageText: string) {
        this.infoMessages = [];
        messageText && this.infoMessages.push(messageText);
    }

    // Method implementation is in progress, the periods population is commented out.
    @action setpulseBetMarketViewModelList() {
        for (let event of this.events) {
            if (event.spLines.length === 3) {
                let pulseComponentData = this.pulsebetsDataForComponents.find(comp => comp.pulseBetMetadata.marketId === event.id);

                if (pulseComponentData) {
                    pulseComponentData.pulseBetMetadata.periods = this.formatPeriods(event.spLines);
                    pulseComponentData.winings.winnings = pulseComponentData.wrappedCalculatedPeriods.map((period, index) => (
                        {
                            winningValue: period.formatedWin,
                            winningStatus: period.periodProgressState ? period.periodProgressState.progressStatus : undefined,
                            periodNumber: index
                        }));
                    pulseComponentData.placeBetHandler = this.getPulseBetMarketHandler(this.isUserLogged, event, pulseComponentData.stakeInputData.stakeValue);
                    pulseComponentData.pulseBetMetadata.isDanger = event.isDanger;
                }
                else {
                    let stakeViewModel = this.createStakeViewModel(event.id);
                    let periods = this.formatPeriods(event.spLines);
                    let duration = periods[2].endTimeInMilli - periods[0].startTimeInMilli;
                    this.pulsebetsDataForComponents.push(new PulseBetMarketComponentData(
                        stakeViewModel,
                        new PulseBetMarketAdditionalData({
                            durationInMilli: duration, name: event.typeName, marketId: event.id, branchID: this.branchId, currencySign: this.currencySign,
                            periods,
                            typeId: event.typeId,
                            isDanger: event.isDanger
                        }),
                        1000000,
                        this.getAvailableFreeBets(event),
                        this.getPulseBetMarketHandler(this.isUserLogged, event, stakeViewModel.stakeValue)
                    ))
                }
            }
        }
    }

    getAvailableFreeBets(event: QAEventInfoExt): PulseFreeBetInfo {
        let freeBets = this.betslipUtil.getAvailableFreeBetsForPulse(event);

        let freePulseBets: PulseFreeBetInfo = Parser.PulseFreeBetInfoParser(event.typeId, freeBets);
        return freePulseBets;
    }

    @Debounce(10)
    @action triggerStakeWinningsAnimation(eventId: string) {
        let pulseBetdata = this.pulsebetsDataForComponents.find(data => data.pulseBetMetadata.marketId === eventId);
        /* istanbul ignore next */
        if (pulseBetdata) {
            /* istanbul ignore next */
            pulseBetdata.onStakeChangeFading = true;
        }
    }

    getPulseBetMarketHandler(isUserLogged: boolean, event: QAEventInfoExt, stakeValue: number): () => void {
        if (isUserLogged) {
            return this.placeBet.bind(this, event.spLines, stakeValue)
        } else {
            return this.navHandler.openResponsiveLogin;
        }
    }

    placeBet(lines: QALiveSlipSelection[], stake: number) {
        this.awaitingCallback.push({
            eventId: lines[0].eventId,
            func: this.betslipUtil.addQAInitilizedSelectionToBetSlip.bind(this.betslipUtil, { lines, stake })
        })

        /* istanbul ignore next */
        this.eventsManager.dispatchEvent("onPulseBetPlacementResult", {
            status: BetPlacementStatus.InProcess,
            eventId: lines[0].eventId,

            ticketId: 0,
            timeInterval: "0-0",
            errorMessage: ""
        }, true);

        if (this.awaitingCallback.length === 1) {
            /* istanbul ignore next */
            this.awaitingCallback[0].func();
        }
    }

    @action.bound formatPeriods(lines: QALiveSlipSelection[]): PulseBetPeriod[] {
        if (!lines || !lines[0]) { return []; }
        let gameProgressMs: number = lines[0].qaParameter1 * 1000;
        let periods: PulseBetPeriod[] = [];
        for (let line of lines) {
            let endTime = (line.qaParameter1 * 1000) - gameProgressMs + (line.qaParameter2 * 1000);
            periods.push({
                europeanOdds: +this.oddsUtil.convertFormat(+line.odds, parseInt(line.eventTypeId, 10), line.lineTypeId, undefined, 1, undefined, parseInt(line.branchId, 10), undefined, undefined),
                startTimeInMilli: (line.qaParameter1 * 1000) - gameProgressMs,
                endTimeInMilli: endTime
            })
        }
        return periods;
    }

    public static get getInstance(): PulseBettingStore {
        return this.instance || (this.instance = new this());
    }

    subscribeToPush(data: any[]) {
        let events: QAEventInfoExt[] = ArrayHelper.ArrayOf(data, Parser.QAEventInfoParser.bind(Parser));
        this.updateStore(events);
        if (this.facade) {
            this.facade.clearDebounce(this.name);
        }
        this.updateBetSlipLines(true);
    }

    @action updateStore(events: QAEventInfo[]) {
        for (let event of events) {
            let ev = this.events.find(
                /* istanbul ignore next */
                evt => evt.id === event.id
            );

            if (!ev) { continue; }
            ev.lines = event.lines;
        }
    }

    @action.bound updateBetSlipLines(isFromPush?: boolean) {
        if (this.events.length > 0) {
            let request: string[] = [];
            let viewKey = 0;
            let responseArray: any[] = [];
            for (let event of this.events) {
                for (let line of event.lines) {
                    request.push([false, viewKey, event.id, event.typeId, line.id, line.odds, 0, 0, false, `0_${this.masterEventId}_0`, false, true, line.lineTypeId].join(","));
                    responseArray[viewKey++] = { event, line };
                }
            }

            this.dataComposite.bettingPageMethods.updateRegularCartItems(request.join("@"), this.successfulUpdateRegularCartItemCallback.bind(this, responseArray), this.errorCallback)
            if (!isFromPush) {
                this.setUpdateEventsData();
            }
        }
    }
    setUpdateEventsData() {
        this.updateEventsTimeout = setTimeout(this.updateBetSlipLines, 5000);
    }

    @action successfullUpdateEventsCallback(response) {
        let data = eval(response);
        this.events = ArrayHelper.ArrayOf(data, Parser.QAEventInfoParser.bind(Parser));

        this.updateBetSlipLines();
    }

    /**istanbuld ignore next */
    @action successfulUpdateRegularCartItemCallback(request: any[], response) {
        let jsonData = eval(response);

        for (let key in jsonData) {
            if (jsonData[key]) {
                if (jsonData[key] !== "-1") {
                    if (request[key]) {
                        let event: QAEventInfoExt = request[key].event;
                        let evt: any = event;
                        evt.masterEventId = this.masterEventId;
                        let item = Parser.QALiveSlipSelectionParser(evt, jsonData[key]);
                        item.lineId = request[key].line.id;
                        item.odds = item.odds || request[key].line.odds;
                        this.parseUpdateRegularCartItem(item);
                    }
                }
                else {
                    this.removeFromPulseBets(request[key].event.id);
                }
            }
        }
        this.setpulseBetMarketViewModelList();

    }

    removeFromPulseBets(eventId: string): void {
        let indexToRemove = this.eventIds.findIndex(eventModel => eventModel.id === eventId);
        if (indexToRemove !== -1) {
            this.eventIds.splice(indexToRemove, 1);
        }
        indexToRemove = this.events.findIndex(qaEvent => qaEvent.id === eventId);
        if (indexToRemove !== -1) {
            this.events.splice(indexToRemove, 1);
        }

        indexToRemove = this.pulsebetsDataForComponents.findIndex(pbComponent => pbComponent.pulseBetMetadata.marketId === eventId);
        if (indexToRemove !== -1) {
            this.pulsebetsDataForComponents[indexToRemove].resetFreePulseBets();
            this.pulsebetsDataForComponents.splice(indexToRemove, 1);
        }
    }

    errorCallback(response) {

    }
    //TODO: write tests
    /*istanbul ignore next */
    @action parseUpdateRegularCartItem(item: QALiveSlipSelection) {
        let observableEvent = this.events.find(ev => ev.id === item.eventId);
        /* istanbul ignore next */
        if (observableEvent) {
            if (observableEvent.spLines.length) {
                let index = observableEvent.spLines.findIndex(spLine => spLine.lineId === item.lineId);
                if (index >= 0) {
                    /* istanbul ignore next */
                    observableEvent.spLines[index] = item;
                }
                else {
                    observableEvent.spLines.push(item);
                }
            }
            else {
                observableEvent.spLines.push(item);
            }

            observableEvent.isDanger = item.danger;
            return observableEvent;
        }
        /* istanbul ignore next */
        return observableEvent;
    }

    /**istanbul ignore next */
    @action setParams(masterEventId: string, events: EventModel[]) {

        if (this.masterEventId === masterEventId && events.length === this.eventIds.length) { return; }
        this.clear();
        this.eventsManager.addEventListener("onBonusMoneyBetting", this, this.addToMessages, true);
        this.eventsManager.addEventListener("onPulseBetWaitingResponse", this, this.removeFromWaitingBets, true);

        this.masterEventId = masterEventId;
        this.eventIds = events;
        let updateString = "";
        for (let item of events) {
            updateString += `${item.id}#${item.type}@`;
        }

        if (updateString.length) {
            this.dataComposite.pageMethods.UpdateEvents(updateString, this.successfullUpdateEventsCallback, this.errorCallback);
        }
    }
}